import { LabeledInfo } from "./LabeledInfo";
import {
  AddOutlined,
  AccountTreeOutlined,
  AssignmentTurnedInOutlined,
  DescriptionOutlined,
  ExpandMore,
  InventoryOutlined,
  PlayArrowOutlined,
  PrintOutlined,
  ReportProblemOutlined,
  UndoOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PreparationTask,
  PreparationWave,
  completePreparationTask,
  createPreparation,
  createPreparationWave,
  loadApplicationSettings,
  loadPreparations,
  loadPreparationWaves,
  loadPreparationWaveHandlingEquipment,
  loadPickingHandlingEquipment,
  loadHandlingUnitDocumentContent,
  loadHandlingUnitDocuments,
  prepareOutputHandlingUnit,
  loadProcessPointMasterData,
  loadStockAllocations,
  reportPreparationException,
  reversePartialPreparationTask,
  resolvePreparationException,
  takePreparationWave,
  takePreparationTask
} from "./api";
import { ListRowActions } from "./ListRowActions";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { PageHeader } from "./PageHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { SourceTrace } from "./SourceTrace";
import { ScanInput } from "./ScanInput";
import { setDiagnosticContext } from "./terminalDiagnostics";
import {
  operationalStatusColor,
  operationalStatusLabel
} from "./operationalStatus";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";
import { ApiError } from "./api/http";
import { requestFefoException } from "./api/fefo";

type PreparationTab = "open" | "progress" | "alerts" | "closed";

function quantity(value: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 6
  }).format(value);
}

function arrivalAge(value: string, t: (key: string, options?: Record<string, unknown>) => string) {
  const elapsedMinutes = Math.max(
    0, Math.floor((Date.now() - new Date(value).getTime()) / 60_000)
  );
  if (elapsedMinutes < 60) return t("preparation.age.minutes", { minutes: elapsedMinutes });
  const hours = Math.floor(elapsedMinutes / 60);
  if (hours < 24) {
    return t("preparation.age.hours", { hours, minutes: elapsedMinutes % 60 });
  }
  const days = Math.floor(hours / 24);
  return t("preparation.age.days", { days, hours: hours % 24 });
}

function escapeHtml(value: string | number | null | undefined): string {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  })[character] ?? character);
}

function printPickingList(wave: import("./api").PreparationWave,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string) {
  const popup = window.open("", "_blank");
  if (!popup) {
    window.alert(t("preparation.print.popupBlocked"));
    return;
  }
  popup.opener = null;
  const rows = wave.steps.map((step, index) => `
    <tr>
      <td>${index + 1}</td><td>${escapeHtml(step.sourceLocationCode)}</td>
      <td>${escapeHtml(step.handlingUnitNumber)}</td><td>${escapeHtml(step.itemCode)}</td>
      <td>${escapeHtml(quantity(step.requestedQuantity, locale))} ${escapeHtml(step.uom)}</td>
      <td></td>
    </tr>`).join("");
  popup.document.write(`<!doctype html><html lang="${locale.startsWith("en") ? "en" : "fr"}"><head><meta charset="utf-8">
    <title>${escapeHtml(t("preparation.print.title", { wave: wave.waveNumber }))}</title>
    <style>
      body{font:14px Arial,sans-serif;margin:24px;color:#17243a}h1{font-size:22px;margin:0 0 8px}
      .meta{margin-bottom:20px;color:#4b5563}table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #9ca3af;padding:8px;text-align:left}th{background:#eef2f7}
      @media print{body{margin:10mm}.no-print{display:none}}
    </style></head><body>
    <button class="no-print" onclick="window.print()">${escapeHtml(t("preparation.print.print"))}</button>
    <h1>${escapeHtml(t("preparation.print.heading", { wave: wave.waveNumber }))}</h1>
    <div class="meta">${escapeHtml(t("preparation.print.warehouse"))} ${escapeHtml(wave.warehouseCode)} · ${escapeHtml(t("preparation.print.destination"))}
      ${escapeHtml(wave.destinationProcessPointCode)} · ${escapeHtml(t("preparation.print.priority"))} ${escapeHtml(wave.priority)}
      ${wave.handlingEquipmentNumber
        ? ` · ${escapeHtml(t("preparation.print.equipment"))} ${escapeHtml(wave.handlingEquipmentNumber)}` : ""}</div>
    <table><thead><tr><th>#</th><th>${escapeHtml(t("preparation.print.location"))}</th><th>HU</th><th>${escapeHtml(t("preparation.print.item"))}</th>
      <th>${escapeHtml(t("preparation.print.quantity"))}</th><th>${escapeHtml(t("preparation.print.check"))}</th></tr></thead><tbody>${rows}</tbody></table>
    </body></html>`);
  popup.document.close();
}

export function PreparationPage({
  onBack,
  initialTab = "open",
  initialTaskId = null,
  initialWaveId = null,
  initialAllocationId = null,
  initialOpenDetail = false,
  onPreparationCreated,
  permissions
}: {
  onBack: () => void;
  initialTab?: PreparationTab;
  initialTaskId?: string | null;
  initialWaveId?: string | null;
  initialAllocationId?: string | null;
  initialOpenDetail?: boolean;
  onPreparationCreated?: () => void;
  permissions: string[];
}) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const client = useQueryClient();
  const [tab, setTab] = useState<PreparationTab>(initialTab);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(Boolean(initialAllocationId));
  const [waveOpen, setWaveOpen] = useState(false);
  const [waveOrderIds, setWaveOrderIds] = useState<string[]>([]);
  const [wavePriority, setWavePriority] = useState("100");
  const [waveEquipmentId, setWaveEquipmentId] = useState("");
  const [waveEquipmentIdentifier, setWaveEquipmentIdentifier] = useState("");
  const [allocationId, setAllocationId] = useState(initialAllocationId ?? "");
  const [stationId, setStationId] = useState("");
  const [priority, setPriority] = useState("100");
  const [selectedTask, setSelectedTask] = useState<PreparationTask | null>(null);
  const [reverseTask, setReverseTask] = useState<PreparationTask | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [mobileSelection, setMobileSelection] = useState<{
    kind: "wave" | "task"; id: string;
  } | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);
  const [expandedWaves, setExpandedWaves] = useState<string[]>([]);
  const initialTaskOpened = useRef(false);
  const reconciledPreparationHuTasks = useRef(new Set<string>());

  const preparations = useQuery({
    queryKey: ["preparations"],
    queryFn: loadPreparations
  });
  const waves = useQuery({
    queryKey: ["preparation-waves"],
    queryFn: loadPreparationWaves
  });
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const waveSequenceMode = settings.data?.find(
    (entry) => entry.key === "PICKING_WAVE_SEQUENCE_MODE"
  )?.value ?? "FLEXIBLE";
  const strictWaveSequence = waveSequenceMode === "STRICT";
  const allocations = useQuery({
    queryKey: ["stock-allocations"],
    queryFn: loadStockAllocations
  });
  const processPoints = useQuery({
    queryKey: ["process-point-master-data"],
    queryFn: loadProcessPointMasterData
  });
  const availableAllocations = (allocations.data ?? []).filter(
    (allocation) => allocation.status === "OPEN"
  );
  const selectedAllocation = availableAllocations.find(
    (allocation) => allocation.id === allocationId
  );
  const stations = (processPoints.data ?? []).filter(
    (point) => point.status === "ACTIVE"
      && point.capabilities.includes("PICKING_STAGING")
      && (!selectedAllocation || point.warehouseId === selectedAllocation.warehouseId)
  );
  const tasks = useMemo(
    () => {
      const taskWaves = new Map<string, {
        number: string;
        status: string;
        sequence: number;
        stepCount: number;
      }>();
      (waves.data ?? []).forEach((wave) =>
        wave.steps.forEach((step) => taskWaves.set(step.preparationTaskId, {
          number: wave.waveNumber,
          status: wave.status,
          sequence: step.sequenceNumber,
          stepCount: wave.steps.length
        }))
      );
      return (preparations.data ?? []).flatMap((order) =>
        order.tasks.map((task) => ({
          ...task,
          outboundOrderNumber: order.outboundOrderNumber,
          outboundOrderLineNumber: order.outboundOrderLineNumber,
          outboundExternalReference: order.outboundExternalReference,
          recipientClientName: order.recipientClientName,
          recipientClientCode: order.recipientClientCode,
          deliveryDestinationName: order.deliveryDestinationName,
          deliveryDestinationCode: order.deliveryDestinationCode,
          preparationWave: taskWaves.get(task.id) ?? null
        }))
      );
    },
    [preparations.data, waves.data]
  );
  const preparationHuDocumentEnabled = settings.data?.find((entry) =>
    entry.key === "PICKING_PREPARATION_HU_DOCUMENT_GENERATION_ENABLED"
  )?.value === "true";
  useEffect(() => {
    if (!preparationHuDocumentEnabled
      || !permissions.includes("picking:execute")) return;
    const candidates = tasks.filter((task) =>
      (task.outputHandlingUnitId || task.status === "IN_PROGRESS")
      && !reconciledPreparationHuTasks.current.has(task.id)
    );
    if (candidates.length === 0) return;
    candidates.forEach((task) => reconciledPreparationHuTasks.current.add(task.id));
    void Promise.allSettled(candidates.map((task) =>
      prepareOutputHandlingUnit(task.id, task.version)
    )).then(() => client.invalidateQueries({ queryKey: ["preparations"] }));
  }, [client, permissions, preparationHuDocumentEnabled, tasks]);

  const openPreparationHuDocument = useMutation({
    mutationFn: async (task: PreparationTask) => {
      if (!task.outputHandlingUnitId) {
        throw new Error(t("preparation.errors.documentUnavailable"));
      }
      let documents = await loadHandlingUnitDocuments(task.outputHandlingUnitId);
      for (let attempt = 0; documents.length === 0 && attempt < 6; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        documents = await loadHandlingUnitDocuments(task.outputHandlingUnitId!);
      }
      const document = documents[0];
      if (!document) throw new Error(t("preparation.errors.documentUnavailable"));
      const preview = window.open("", "_blank");
      try {
        const blob = await loadHandlingUnitDocumentContent(
          task.outputHandlingUnitId, document.id
        );
        const url = URL.createObjectURL(blob);
        if (preview) preview.location.href = url;
        else window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (cause) {
        preview?.close();
        throw cause;
      }
    }
  });
  useEffect(() => {
    if (!initialTaskId || initialTaskOpened.current) return;
    const target = tasks.find((task) => task.id === initialTaskId);
    if (target) {
      initialTaskOpened.current = true;
      setTab(target.status === "EXCEPTION" ? "alerts"
        : ["COMPLETED", "CANCELLED"].includes(target.status) ? "closed"
          : target.status === "IN_PROGRESS"
            || target.preparationWave?.status === "IN_PROGRESS"
            ? "progress" : "open");
      if (initialOpenDetail) setSelectedTask(target);
      window.setTimeout(() => document.getElementById(
        `preparation-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    }
  }, [initialOpenDetail, initialTaskId, tasks]);
  const taskTab = (task: typeof tasks[number]) => {
    if (task.status === "EXCEPTION") return "alerts";
    if (["COMPLETED", "CANCELLED"].includes(task.status)) return "closed";
    if (task.status === "IN_PROGRESS"
      || task.preparationWave?.status === "IN_PROGRESS") return "progress";
    return "open";
  };
  const waveTab = (status: string): PreparationTab => {
    if (status === "EXCEPTION") return "alerts";
    if (["COMPLETED", "CANCELLED"].includes(status)) return "closed";
    if (status === "IN_PROGRESS") return "progress";
    return "open";
  };
  useEffect(() => {
    if (!initialWaveId) return;
    const target = (waves.data ?? []).find((wave) => wave.id === initialWaveId);
    if (!target) return;
    setTab(waveTab(target.status));
    setExpandedWaves((current) => current.includes(target.id)
      ? current : [...current, target.id]);
  }, [initialWaveId, waves.data]);
  const groupedTaskIds = useMemo(() => new Set(
    (waves.data ?? []).flatMap((wave) =>
      wave.steps.map((step) => step.preparationTaskId)
    )
  ), [waves.data]);
  const filtered = useMemo(() => tasks.filter((task) => {
    if (groupedTaskIds.has(task.id)) return false;
    const statusMatches = taskTab(task) === tab;
    const value = (`${task.taskNumber} ${task.orderNumber} ${task.itemCode} `
      + `${task.handlingUnitNumber} ${task.sourceLocationCode} `
      + `${task.destinationProcessPointCode} `
      + `${task.preparationWave?.number ?? ""} `
      + `${task.outboundOrderNumber ?? ""} `
      + `${task.outboundExternalReference ?? ""} `
      + `${task.recipientClientName ?? task.recipientClientCode ?? ""} `
      + `${task.deliveryDestinationName
        ?? task.deliveryDestinationCode ?? ""}`).toLocaleLowerCase("fr");
    return statusMatches
      && (!search.trim() || value.includes(search.trim().toLocaleLowerCase("fr")));
  }), [groupedTaskIds, search, tab, tasks]);
  const filteredWaves = useMemo(() => (waves.data ?? []).filter((wave) => {
    const value = `${wave.waveNumber} ${wave.warehouseCode} `
      + `${wave.clientCode ?? ""} ${wave.destinationProcessPointCode} `
      + `${wave.orderNumbers.join(" ")} `
      + wave.steps.map((step) =>
        `${step.taskNumber} ${step.handlingUnitNumber} ${step.itemCode} `
        + `${step.sourceLocationCode}`
      ).join(" ");
    return waveTab(wave.status) === tab
      && (!search.trim()
        || value.toLocaleLowerCase("fr")
          .includes(search.trim().toLocaleLowerCase("fr")));
  }), [search, tab, waves.data]);
  useEffect(() => setMobileSelection(null), [tab]);

  const create = useMutation({
    mutationFn: () => createPreparation(
      allocationId, stationId, Number(priority)
    ),
    onSuccess: async () => {
      setCreateOpen(false);
      setAllocationId("");
      setStationId("");
      setPriority("100");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["preparations"] }),
        client.invalidateQueries({ queryKey: ["stock-allocations"] })
      ]);
      onPreparationCreated?.();
    }
  });
  const createWave = useMutation({
    mutationFn: () => createPreparationWave(
      waveOrderIds, Number(wavePriority), waveEquipmentId || null,
      waveEquipmentIdentifier.trim() || null
    ),
    onSuccess: async () => {
      setWaveOpen(false);
      setWaveOrderIds([]);
      setWavePriority("100");
      setWaveEquipmentId("");
      setWaveEquipmentIdentifier("");
      await client.invalidateQueries({ queryKey: ["preparation-waves"] });
    }
  });
  const takeWave = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      takePreparationWave(id, version),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["preparation-waves"] }),
        client.invalidateQueries({ queryKey: ["preparations"] })
      ]);
    }
  });
  const reversePartial = useMutation({
    mutationFn: () => reversePartialPreparationTask(
      reverseTask!.id, reverseReason.trim(), reverseTask!.version
    ),
    onSuccess: async () => {
      setReverseTask(null);
      setReverseReason("");
      setTab("progress");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["preparations"] }),
        client.invalidateQueries({ queryKey: ["preparation-waves"] }),
        client.invalidateQueries({ queryKey: ["stock"] }),
        client.invalidateQueries({ queryKey: ["stock-movements"] })
      ]);
    }
  });
  const groupedOrderIds = new Set(
    (waves.data ?? []).flatMap((wave) => wave.preparationOrderIds)
  );
  const groupableOrders = (preparations.data ?? []).filter(
    (order) => order.status === "OPEN" && !groupedOrderIds.has(order.id)
  );
  const waveOrderGroups = useMemo(() => {
    const groups = new Map<string, {
      key: string;
      outboundOrderNumber: string | null;
      externalReference: string | null;
      recipient: string | null;
      destination: string | null;
      requestedDeliveryTo: string | null;
      orders: typeof groupableOrders;
    }>();
    groupableOrders.forEach((order) => {
      const key = order.outboundOrderId ?? `PREPARATION:${order.id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.orders.push(order);
        return;
      }
      groups.set(key, {
        key,
        outboundOrderNumber: order.outboundOrderNumber,
        externalReference: order.outboundExternalReference,
        recipient: order.recipientClientName ?? order.recipientClientCode,
        destination: order.deliveryDestinationName
          ?? order.deliveryDestinationCode,
        requestedDeliveryTo: order.requestedDeliveryTo,
        orders: [order]
      });
    });
    return [...groups.values()];
  }, [groupableOrders]);
  const selectedWaveOrders = groupableOrders.filter(
    (order) => waveOrderIds.includes(order.id)
  );
  const waveEquipment = useQuery({
    queryKey: ["preparation-wave-equipment", [...waveOrderIds].sort().join(",")],
    queryFn: () => loadPreparationWaveHandlingEquipment(waveOrderIds),
    enabled: waveOpen && waveOrderIds.length > 0
  });
  const suitableWaveEquipment = (waveEquipment.data ?? []).filter(
    (item) => item.suitable
  );
  useEffect(() => {
    setWaveEquipmentId("");
    setWaveEquipmentIdentifier("");
  }, [waveOpen, waveOrderIds.join(",")]);
  const groupingEnabled = settings.data?.find(
    (entry) => entry.key === "PICKING_ORDER_GROUPING_ENABLED"
  )?.value === "true";
  const groupableTaskCount = groupableOrders.reduce(
    (total, order) => total + order.tasks.length, 0
  );
  const waveDisabled = !groupingEnabled || groupableTaskCount < 2;
  const waveDisabledReason = !groupingEnabled
    ? t("preparation.help.groupingDisabled")
    : groupableTaskCount < 2
      ? t("preparation.help.minimumPicks")
      : "";
  useEffect(() => {
    if (!groupingEnabled) {
      setWaveOpen(false);
      setWaveOrderIds([]);
    }
  }, [groupingEnabled]);
  const refresh = async () => {
    setSelectedTask(null);
    await Promise.all([
      client.invalidateQueries({ queryKey: ["preparations"] }),
      client.invalidateQueries({ queryKey: ["preparation-waves"] }),
      client.invalidateQueries({ queryKey: ["stock-allocations"] }),
      client.invalidateQueries({ queryKey: ["stock-by-item"] })
    ]);
  };
  const tabCounts = (value: PreparationTab) =>
    tasks.filter((task) => !groupedTaskIds.has(task.id)
      && taskTab(task) === value).length
    + (waves.data ?? []).filter((wave) => waveTab(wave.status) === value).length;
  const statusTabs = [
    { value: "open" as const, label: t("preparation.tabs.open"), mobileLabel: t("preparation.tabsMobile.open"),
      count: tabCounts("open") },
    { value: "progress" as const, label: t("preparation.tabs.progress"), mobileLabel: t("preparation.tabsMobile.progress"),
      count: tabCounts("progress") },
    { value: "alerts" as const, label: t("preparation.tabs.alerts"), mobileLabel: t("preparation.tabsMobile.alerts"),
      count: tabCounts("alerts") },
    { value: "closed" as const, label: t("preparation.tabs.closed"), mobileLabel: t("preparation.tabsMobile.closed"),
      count: tabCounts("closed") }
  ];
  const selectedMobileWave: PreparationWave | null = mobileSelection?.kind === "wave"
    ? (waves.data ?? []).find((wave) => wave.id === mobileSelection.id
      && waveTab(wave.status) === tab) ?? null
    : null;
  const selectedMobileTask = mobileSelection?.kind === "task"
    ? tasks.find((task) => task.id === mobileSelection.id
      && taskTab(task) === tab) ?? null : null;
  const selectedWaveExecutableTask = selectedMobileWave?.status === "IN_PROGRESS"
    ? selectedMobileWave.steps.map((step) => tasks.find((task) =>
      task.id === step.preparationTaskId)).find((task) =>
      task?.status === "IN_PROGRESS") ?? null : null;
  const mobilePrimaryLabel = selectedMobileWave?.status === "CREATED"
    ? t("preparation.actions.take")
    : selectedWaveExecutableTask ? t("preparation.actions.execute")
      : selectedMobileTask?.status === "IN_PROGRESS" ? t("preparation.actions.confirm")
      : selectedMobileTask?.status === "OPEN" ? t("preparation.actions.take")
        : selectedMobileTask?.status === "EXCEPTION" ? t("preparation.actions.viewAlert")
          : t("preparation.actions.select");
  const mobileActionTask = selectedWaveExecutableTask ?? selectedMobileTask;
  const mobilePrimaryAvailable = permissions.includes("picking:execute")
    && (selectedMobileWave?.status === "CREATED"
      || Boolean(mobileActionTask
        && !["COMPLETED", "CANCELLED"].includes(mobileActionTask.status)));
  useEffect(() => {
    const task = selectedTask ?? mobileActionTask;
    const wave = selectedMobileWave ?? (task
      ? (waves.data ?? []).find((candidate) => candidate.steps.some((step) =>
        step.preparationTaskId === task.id)) ?? null : null);
    setDiagnosticContext({
      screen: t("preparation.module"),
      action: selectedTask ? t("preparation.actions.execute")
        : waveOpen ? t("preparation.labels.createWaveDialog")
          : createOpen ? t("preparation.labels.createOrderDialog")
            : `${t("preparation.module")} · ${t(`preparation.tabs.${tab}`)}`,
      entities: [
        ...(wave ? [{ type: "WAVE" as const, identifier: wave.waveNumber,
          status: wave.status }] : []),
        ...(task ? [
          { type: "PICK" as const, identifier: task.taskNumber, status: task.status },
          { type: "HU" as const, identifier: task.handlingUnitNumber },
          { type: "ORDER" as const, identifier: task.orderNumber }
        ] : [])
      ]
    });
  }, [selectedTask, mobileActionTask, selectedMobileWave, waves.data,
    waveOpen, createOpen, tab]);
  const executeMobilePrimary = () => {
    if (selectedMobileWave?.status === "CREATED") {
      takeWave.mutate({ id: selectedMobileWave.id, version: selectedMobileWave.version });
      return;
    }
    const task = mobileActionTask;
    if (task && !["COMPLETED", "CANCELLED"].includes(task.status)) {
      setSelectedTask(task);
    }
  };

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" },
      minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("preparation.module")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("preparation.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("preparation.module")} compact onBack={onBack}
          actions={permissions.includes("picking:create") ? (
            <Stack direction="row" spacing={1}>
              <Tooltip title={waveDisabledReason}>
                <span>
                  <Button variant="outlined"
                    data-testid="preparation-wave-new"
                    disabled={waveDisabled}
                    onClick={() => setWaveOpen(true)}>
                    {t("preparation.actions.createWave")}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddOutlined />}
                data-testid="preparation-new"
                onClick={() => setCreateOpen(true)}>
                {t("preparation.actions.new")}
              </Button>
            </Stack>
          ) : undefined} />
      </Box>
      <ResponsiveStatusTabs value={tab} items={statusTabs} onChange={setTab} />
      {tab === "open" && !settings.isPending && !groupingEnabled
        && groupableTaskCount >= 2 && (
        <Alert severity="warning" sx={{ flexShrink: 0 }}>
          {t("preparation.help.waveDisabled")}
        </Alert>
      )}
      {takeWave.isError && (
        <Alert severity="error" sx={{ flexShrink: 0 }}>
          {takeWave.error instanceof Error
            ? takeWave.error.message
            : t("preparation.errors.takeWave")}
        </Alert>
      )}
      <Card sx={{ p: 1.25, flexShrink: 0,
        display: { xs: "none", sm: "block" } }}>
        <TextField fullWidth size="small" label={t("common.filters.search")}
          placeholder={t("preparation.searchWide")}
          value={search} onChange={(event) => setSearch(event.target.value)} />
      </Card>
      <Box sx={{
        flex: { xs: "1 1 0", sm: "none", md: "1 1 0" }, minHeight: 0,
        overflowY: { xs: "auto", sm: "visible", md: "auto" }, pr: .5,
        pb: { xs: 11, md: 0 }
      }}>
        <Stack spacing={1}>
          {preparations.isError && (
            <Alert severity="error">{t("preparation.errors.load")}</Alert>
          )}
          {!preparations.isLoading && filtered.length === 0
            && filteredWaves.length === 0 && (
            <Alert severity="info">{t("preparation.empty.list")}</Alert>
          )}
          {filteredWaves.map((wave) => {
            const expanded = expandedWaves.includes(wave.id);
            const waveTasks = wave.steps.map((step) =>
              tasks.find((task) => task.id === step.preparationTaskId)
            ).filter((task): task is typeof tasks[number] => Boolean(task));
            return (
              <Card key={wave.id} tabIndex={0} role="button"
                aria-pressed={mobileSelection?.kind === "wave"
                  && mobileSelection.id === wave.id}
                onClick={() => setMobileSelection({ kind: "wave", id: wave.id })}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setMobileSelection({ kind: "wave", id: wave.id });
                  }
                }} sx={{
                borderLeft: 4,
                borderLeftColor: wave.status === "EXCEPTION"
                  ? "error.main" : "secondary.main",
                outline: { xs: mobileSelection?.kind === "wave"
                  && mobileSelection.id === wave.id
                  ? "2px solid" : "none", sm: "none" },
                outlineColor: "primary.main"
              }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                  alignItems={{ md: "center" }} sx={{ p: 1.5 }}>
                  <AccountTreeOutlined color={
                    wave.status === "EXCEPTION" ? "error" : "secondary"
                  } />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={800}>
                      {wave.waveNumber} · {t("preparation.labels.preparationTour")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("preparation.labels.picks", { count: wave.steps.length })} ·{" "}
                      {t("preparation.labels.outboundOrders", { count: wave.orderNumbers.length })} ·{" "}
                      {wave.warehouseCode} → {wave.destinationProcessPointCode}
                      {" · "}{t("preparation.labels.priority", { priority: wave.priority })}
                      {" · "}{arrivalAge(wave.createdAt, t)}
                      {wave.status === "IN_PROGRESS" && strictWaveSequence && (() => {
                        const current = wave.steps.findIndex((step) =>
                          !["COMPLETED", "SKIPPED", "CANCELLED"]
                            .includes(step.status)
                        );
                        return current >= 0
                          ? ` · ${t("preparation.labels.step", { current: current + 1, total: wave.steps.length })}` : "";
                      })()}
                      {wave.status === "IN_PROGRESS" && !strictWaveSequence
                        ? ` · ${t("preparation.labels.freeOrder")}` : ""}
                      {wave.handlingEquipmentNumber
                        ? ` · ${wave.handlingEquipmentTypeName} ${wave.handlingEquipmentNumber}`
                        : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wave.clientCode
                        ? `${t("preparation.labels.client", { client: wave.clientCode })} · ` : ""}
                      {t("preparation.labels.route")} {wave.destinationProcessPointCode} →{" "}
                      {wave.steps.map((step) =>
                        step.sourceLocationCode).join(" → ")}
                      {" → "}{wave.destinationProcessPointCode}
                      {" · "}{t("preparation.labels.distance", { distance: wave.estimatedDistanceUnits })}
                      {" ("}{t("preparation.labels.returnDistance", { distance: wave.returnDistanceUnits })}{")"}
                    </Typography>
                    <SourceTrace sources={waveTasks.map(
                      (task) => task.outboundOrderNumber
                    )} step={t("preparation.labels.waveStep", { number: wave.waveNumber })}
                      client={wave.clientCode}
                      destination={wave.destinationProcessPointCode} />
                  </Box>
                  <IconButton className="mobile-card-expand" aria-label={
                    expanded ? t("preparation.aria.hidePicks")
                      : t("preparation.aria.showPicks")
                  } onClick={(event) => {
                    event.stopPropagation();
                    setExpandedWaves((current) => expanded
                      ? current.filter((id) => id !== wave.id)
                      : [...current, wave.id]);
                  }}>
                    <ExpandMore sx={{
                      transition: "transform 160ms ease",
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)"
                    }} />
                  </IconButton>
                  <ListRowActions status={<Chip label={operationalStatusLabel(wave.status)} color={
                    operationalStatusColor(wave.status)
                 } />} actions={(
                    <Stack direction="row" spacing={1}
                      sx={{ display: { xs: "none", sm: "flex" } }}>
                      <Button variant="outlined" startIcon={<PrintOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          printPickingList(wave, locale, t);
                        }}>
                        {t("preparation.actions.pickingList")}
                      </Button>
                      {wave.status === "CREATED" && <Button variant="outlined"
                        startIcon={<PlayArrowOutlined />}
                        disabled={takeWave.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          takeWave.mutate({
                          id: wave.id, version: wave.version
                          });
                        }}>
                        {t("preparation.actions.take")}
                      </Button>}
                    </Stack>
                  )} />
                </Stack>
                <Collapse in={expanded}>
                  <Box sx={{
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover"
                  }}>
                    {waveTasks.map((task, index) => (
                      <Stack key={task.id} direction={{
                        xs: "column", md: "row"
                      }} spacing={1.5} alignItems={{ md: "center" }}
                        sx={{
                          px: 2, py: 1.25,
                          borderBottom: index < waveTasks.length - 1 ? 1 : 0,
                          borderColor: "divider"
                        }}>
                        <Typography fontWeight={800} sx={{ minWidth: 90 }}>
                          {index + 1}/{waveTasks.length}
                        </Typography>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography fontWeight={700}>
                            {task.taskNumber} · {task.handlingUnitNumber}
                            {" · "}{task.itemCode}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {task.sourceLocationCode}
                            {" · "}{t("preparation.labels.sequence", { sequence:
                              wave.steps[index]?.sourceTravelSequence
                                ?? t("preparation.labels.undefined") })}
                            {" · "}{t("preparation.labels.distance", { distance:
                              wave.steps[index]?.distanceFromPrevious ?? 0 })}
                            {" · "}{quantity(task.requestedQuantity, locale)} {task.uom}
                          </Typography>
                        </Box>
                        {wave.status === "IN_PROGRESS"
                          && task.status === "IN_PROGRESS" && (
                          <Button variant="outlined"
                            sx={{ display: { xs: "none", sm: "inline-flex" } }}
                            onClick={() => setSelectedTask(task)}>
                            {t("preparation.actions.execute")}
                          </Button>
                        )}
                        <Chip label={
                          wave.steps[index]?.status === "PENDING" ? t("preparation.labels.upcoming")
                            : task.status === "OPEN" ? t("preparation.tabs.open")
                              : task.status === "IN_PROGRESS"
                                ? strictWaveSequence ? operationalStatusLabel(task.status) : t("preparation.labels.available")
                                : operationalStatusLabel(task.status)
                        } />
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Card>
            );
          })}
          {filtered.map((task) => (
            <Card key={task.id} id={`preparation-${task.id}`}
              tabIndex={0} role="button"
              aria-pressed={mobileSelection?.kind === "task"
                && mobileSelection.id === task.id}
              onClick={() => setMobileSelection({ kind: "task", id: task.id })}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setMobileSelection({ kind: "task", id: task.id });
                }
              }} sx={{
              p: 1.5,
              borderColor: task.id === initialTaskId ? "primary.main" : undefined,
              borderWidth: task.id === initialTaskId ? 2 : undefined,
              borderLeftColor: task.status === "EXCEPTION"
                ? "error.main" : "secondary.main",
              outline: { xs: mobileSelection?.kind === "task"
                && mobileSelection.id === task.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main"
            }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ md: "center" }}>
                <InventoryOutlined color={
                  task.status === "EXCEPTION" ? "error" : "secondary"
                } />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                      <LabeledInfo label={t("preparation.card.task")} prominent>{task.taskNumber}</LabeledInfo>
                      <LabeledInfo label={t("preparation.labels.priorityField")}>{task.priority}</LabeledInfo>
                      <LabeledInfo label={t("preparation.card.age")}>{arrivalAge(task.createdAt, t)}</LabeledInfo>
                    </Stack>
                    <Box sx={{ p: 1.5, bgcolor: "background.default", borderRadius: 1 }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                          <LabeledInfo label={t("preparation.labels.item")} prominent>{task.itemCode}</LabeledInfo>
                          {task.itemName && <LabeledInfo label={t("preparation.card.description")} prominent>{task.itemName}</LabeledInfo>}
                          <LabeledInfo label={t("preparation.card.quantity")} prominent>
                            {quantity(task.requestedQuantity, locale)} {task.uom}
                          </LabeledInfo>
                        </Stack>
                        <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1} alignItems="center">
                          <LabeledInfo label={t("preparation.card.source")} prominent>{task.sourceLocationCode}</LabeledInfo>
                          <Typography aria-hidden="true" color="text.secondary">→</Typography>
                          <LabeledInfo label={t("preparation.card.target")} prominent>{task.destinationProcessPointCode}</LabeledInfo>
                          <LabeledInfo label={t("preparation.card.hu")}>{task.handlingUnitNumber}</LabeledInfo>
                        </Stack>
                      </Stack>
                    </Box>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                      {(task.recipientClientName || task.recipientClientCode) && (
                        <LabeledInfo label={t("preparation.card.client")}>{task.recipientClientName ?? task.recipientClientCode}</LabeledInfo>
                      )}
                      {(task.deliveryDestinationName || task.deliveryDestinationCode) && (
                        <LabeledInfo label={t("preparation.card.destination")}>{task.deliveryDestinationName ?? task.deliveryDestinationCode}</LabeledInfo>
                      )}
                      {task.outboundOrderNumber && (
                        <LabeledInfo label={t("preparation.card.order")}>{task.outboundOrderNumber}</LabeledInfo>
                      )}
                      {task.outboundOrderLineNumber != null && (
                        <LabeledInfo label={t("preparation.card.line")}>{task.outboundOrderLineNumber}</LabeledInfo>
                      )}
                      {task.outboundExternalReference && (
                        <LabeledInfo label={t("preparation.card.reference")}>{task.outboundExternalReference}</LabeledInfo>
                      )}
                      <LabeledInfo label={t("preparation.card.preparation")}>{task.orderNumber}</LabeledInfo>
                      {task.preparationWave && (
                        <LabeledInfo label={t("preparation.card.wave")}>
                          {task.preparationWave.number} · {t("preparation.labels.step", {
                            current: task.preparationWave.sequence, total: task.preparationWave.stepCount
                          })}
                        </LabeledInfo>
                      )}
                      {task.routeSequence != null && (
                        <LabeledInfo label={t("preparation.labels.routeField")}>
                          {task.routeNumber} · {t("preparation.labels.routeStep", { step: task.routeSequence })}
                        </LabeledInfo>
                      )}
                    </Stack>
                    {task.exceptionCode && (
                      <Alert severity="error">
                        <strong>{t(`preparation.exceptions.${task.exceptionCode}`, { defaultValue: task.exceptionCode })}</strong>
                        {task.exceptionComment && <Box>{task.exceptionComment}</Box>}
                      </Alert>
                    )}
                  </Stack>
                  {task.status === "COMPLETED" && task.outputHandlingUnitNumber && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                      sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                      <Chip variant="outlined" label={
                        `${t("preparation.labels.sourceHu")} ${task.handlingUnitNumber}`
                        + (task.sourceQuantityBeforePicking == null ? "" : ` · ${t(
                          "preparation.labels.stockBefore", { quantity: quantity(
                            task.sourceQuantityBeforePicking, locale), uom: task.uom }
                        )}`)
                        + (task.sourceQuantityRemaining == null ? "" : ` · ${t(
                          "preparation.labels.stockRemaining", { quantity: quantity(
                            task.sourceQuantityRemaining, locale), uom: task.uom }
                        )}`)
                      } />
                      <Chip variant="outlined" color="secondary" label={
                        `${t("preparation.labels.preparationHu")} ${task.outputHandlingUnitNumber}`
                        + ` · ${task.outputHandlingUnitId === task.handlingUnitId
                          ? t("preparation.labels.fullHuTransferred")
                          : t("preparation.labels.transferredQuantity", { quantity: quantity(
                            task.pickedQuantity, locale), uom: task.uom })}`
                      } />
                    </Stack>
                  )}
                </Box>
                <ListRowActions actions={permissions.includes("picking:execute") ? (
                  <Stack direction="row" spacing={1}>
                    {preparationHuDocumentEnabled && task.outputHandlingUnitId && (
                      <Button variant="outlined" startIcon={<DescriptionOutlined />}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={openPreparationHuDocument.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          openPreparationHuDocument.mutate(task);
                        }}>
                        {t("preparation.actions.viewOutputHuDocument")}
                      </Button>
                    )}
                    {task.status === "COMPLETED"
                      && task.outputHandlingUnitId
                      && task.outputHandlingUnitId !== task.handlingUnitId ? (
                      <Button variant="outlined" color="warning"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        startIcon={<UndoOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setReverseTask(task);
                        }}>
                        {t("preparation.actions.correctPartialPicking")}
                      </Button>
                    ) : !["COMPLETED", "CANCELLED"].includes(task.status) ? (
                      <Button variant="outlined"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        data-testid={`preparation-action-${task.id}`}
                        startIcon={task.status === "OPEN"
                          ? <PlayArrowOutlined /> : task.status === "EXCEPTION"
                            ? <ReportProblemOutlined /> : <AssignmentTurnedInOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedTask(task);
                        }}>
                        {task.status === "OPEN" ? t("preparation.actions.take")
                          : task.status === "IN_PROGRESS"
                            ? t("preparation.actions.confirm") : t("preparation.actions.view")}
                      </Button>
                    ) : null}
                  </Stack>
                ) : undefined} status={<Chip label={operationalStatusLabel(task.status)}
                  color={operationalStatusColor(task.status)} />} />
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      <MobileActionBar
        secondaryIcon={<AddOutlined />}
        secondaryLabel={t("preparation.actions.new")}
        secondaryDisplayLabel={t("preparation.actions.newShort")}
        onSecondary={permissions.includes("picking:create")
          ? () => setCreateOpen(true) : undefined}
        additionalIcon={<AccountTreeOutlined />}
        additionalLabel={t("preparation.actions.createWave")}
        additionalDisplayLabel={t("preparation.actions.waveShort")}
        onAdditional={permissions.includes("picking:create")
          ? () => setWaveOpen(true) : undefined}
        additionalDisabled={waveDisabled}
        primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary}
        onMore={selectedMobileWave ? setMobileMenuAnchor : undefined}
      />
      <Dialog open={Boolean(reverseTask)}
        onClose={() => !reversePartial.isPending && setReverseTask(null)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("preparation.labels.reversePartialTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("preparation.help.reversePartial", {
                output: reverseTask?.outputHandlingUnitNumber,
                source: reverseTask?.handlingUnitNumber
              })}
            </Alert>
            <TextField autoFocus required multiline minRows={3}
              label={t("preparation.labels.correctionReason")}
              value={reverseReason} inputProps={{ maxLength: 500 }}
              onChange={(event) => setReverseReason(event.target.value)}
              helperText={t("preparation.help.requiredThree")} />
            {reversePartial.isError && <Alert severity="error">
              {reversePartial.error instanceof Error
                ? reversePartial.error.message : t("preparation.errors.action")}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={reversePartial.isPending}
            onClick={() => setReverseTask(null)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained" color="warning"
            disabled={reversePartial.isPending || reverseReason.trim().length < 3}
            onClick={() => reversePartial.mutate()}>
            {t("preparation.actions.confirmCorrection")}
          </Button>
        </DialogActions>
      </Dialog>
      <Menu anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)}
        onClose={() => setMobileMenuAnchor(null)}>
        {selectedMobileWave && (
          <MenuItem onClick={() => {
            setMobileMenuAnchor(null);
            printPickingList(selectedMobileWave, locale, t);
          }}>
            {t("preparation.actions.pickingList")}
          </MenuItem>
        )}
      </Menu>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("preparation.labels.createOrderDialog")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField select required label={t("preparation.labels.reservedAllocation")}
              data-testid="preparation-allocation"
              value={allocationId} onChange={(event) => {
                setAllocationId(event.target.value);
                setStationId("");
              }}>
              {availableAllocations.map((allocation) => (
                <MenuItem key={allocation.id} value={allocation.id}>
                  {allocation.requestReference} · {allocation.itemCode}
                  {" · "}{quantity(allocation.requestedQuantity, locale)} {allocation.uom}
                </MenuItem>
              ))}
            </TextField>
            <TextField select required label={t("preparation.labels.consolidationStation")}
              data-testid="preparation-station"
              value={stationId}
              onChange={(event) => setStationId(event.target.value)}>
              {stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>
                  {station.code} · {station.displayName}
                </MenuItem>
              ))}
            </TextField>
            <TextField type="number" label={t("preparation.labels.priorityField")} value={priority}
              inputProps={{ min: 0 }}
              onChange={(event) => setPriority(event.target.value)} />
            {!availableAllocations.length && (
              <Alert severity="info">
                {t("preparation.empty.allocations")}
              </Alert>
            )}
            {selectedAllocation && !stations.length && (
              <Alert severity="warning">
                {t("preparation.empty.stations")}
              </Alert>
            )}
            {create.isError && <Alert severity="error">
              {create.error instanceof Error
                ? create.error.message : t("preparation.errors.create")}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained" disabled={!allocationId || !stationId}
            data-testid="preparation-create-confirm"
            onClick={() => create.mutate()}>
            {t("preparation.actions.createTasks")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waveOpen} onClose={() => setWaveOpen(false)}
        fullWidth maxWidth="md">
        <DialogTitle>{t("preparation.labels.createWaveDialog")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity="info">
              {t("preparation.help.waveInstructions")}
            </Alert>
            {waveOrderGroups.map((group) => {
              const ids = group.orders.map((order) => order.id);
              const selectedCount = ids.filter(
                (id) => waveOrderIds.includes(id)
              ).length;
              const allSelected = selectedCount === ids.length;
              return (
                <Card key={group.key} variant="outlined" sx={{ p: 1.25 }}>
                  <FormControlLabel sx={{ m: 0, width: "100%" }}
                    control={<Checkbox checked={allSelected}
                      indeterminate={selectedCount > 0 && !allSelected}
                      onChange={(event) => setWaveOrderIds((current) => {
                        const withoutGroup = current.filter(
                          (id) => !ids.includes(id)
                        );
                        return event.target.checked
                          ? [...withoutGroup, ...ids] : withoutGroup;
                      })} />}
                    label={<Box sx={{ width: "100%" }}>
                      <Typography fontWeight={800}>
                        {group.outboundOrderNumber
                          ?? group.orders[0].orderNumber}
                        {group.externalReference
                          ? ` · ${group.externalReference}` : ""}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[group.recipient, group.destination,
                          t("preparation.labels.preparations", { count: group.orders.length }),
                          t("preparation.labels.tasks", { count: group.orders.reduce(
                            (total, order) => total + order.tasks.length, 0
                          ) })]
                          .filter(Boolean).join(" · ")}
                        {group.requestedDeliveryTo
                          ? ` · ${t("preparation.labels.deliveryBefore", { date: new Date(
                            group.requestedDeliveryTo
                          ).toLocaleString(locale) })}` : ""}
                      </Typography>
                    </Box>} />
                  <Stack spacing={0.5} sx={{ pl: 5 }}>
                    {group.orders.map((order) => (
                      <FormControlLabel key={order.id} sx={{ m: 0 }}
                        control={<Checkbox size="small"
                          checked={waveOrderIds.includes(order.id)}
                          onChange={(event) => setWaveOrderIds((current) =>
                            event.target.checked
                              ? [...new Set([...current, order.id])]
                              : current.filter((id) => id !== order.id)
                          )} />}
                        label={
                          `${order.outboundOrderLineNumber
                            ? `${t("preparation.labels.orderLine", { line: order.outboundOrderLineNumber })} · ` : ""}`
                          + `${order.itemCode} — ${order.itemName} · `
                          + `${t("preparation.labels.huTasks", { count: order.tasks.length })} · `
                          + `${order.warehouseCode} → `
                          + `${order.destinationProcessPointCode}`
                        } />
                    ))}
                  </Stack>
                </Card>
              );
            })}
            <Alert severity={selectedWaveOrders.reduce(
              (total, order) => total + order.tasks.length, 0
            ) >= 2
              ? "success" : "warning"}>
              {t("preparation.labels.currentSelection", {
                preparations: t("preparation.labels.preparations", { count: selectedWaveOrders.length }),
                tasks: t("preparation.labels.tasks", { count: selectedWaveOrders.reduce(
                  (total, order) => total + order.tasks.length, 0
                ) }),
                articles: t("preparation.labels.articles", { count:
                  new Set(selectedWaveOrders.map((order) => order.itemCode)).size })
              })}
            </Alert>
            <TextField type="number" label={t("preparation.labels.wavePriority")}
              value={wavePriority} inputProps={{ min: 0 }}
              onChange={(event) => setWavePriority(event.target.value)} />
            <ScanInput
              options={suitableWaveEquipment.map((item) => ({
                value: item.equipmentNumber,
                label: `${item.equipmentNumber} · ${item.typeName}`
                  + ` · ${t("preparation.labels.load", { load: formatMeasurement(
                    item.requiredLoadKg, "MASS", measurements, locale) })}`
                  + (item.maxLoadKg == null ? ""
                    : ` / ${t("preparation.labels.capacity", {
                      capacity: formatMeasurement(item.maxLoadKg, "MASS",
                        measurements, locale)
                    })}`)
              }))}
              value={waveEquipmentIdentifier}
              disabled={waveEquipment.isLoading || waveOrderIds.length === 0}
              onChange={(value) => {
                setWaveEquipmentIdentifier(value);
                const selected = suitableWaveEquipment.find((item) =>
                  item.equipmentNumber.toUpperCase() === value.trim().toUpperCase());
                setWaveEquipmentId(selected?.id ?? "");
              }}
              required label={t("preparation.labels.handlingEquipment")}
              helperText={waveEquipment.isLoading
                ? t("preparation.help.equipmentLoading")
                  : suitableWaveEquipment.length > 0
                  ? t("preparation.help.equipmentChoose")
                    : waveOrderIds.length > 0
                    ? t("preparation.help.equipmentNone")
                    : t("preparation.help.selectPreparations")}
            />
            {waveEquipment.isError && <Alert severity="error">
              {waveEquipment.error instanceof Error
                ? waveEquipment.error.message
                : t("preparation.errors.equipment")}
            </Alert>}
            {createWave.isError && (
              <Alert severity="error">
                {createWave.error instanceof Error
                  ? createWave.error.message : t("preparation.errors.createWave")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaveOpen(false)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained"
            data-testid="preparation-wave-create-confirm"
            disabled={!groupingEnabled || selectedWaveOrders.reduce(
              (total, order) => total + order.tasks.length, 0
            ) < 2
              || createWave.isPending || !waveEquipmentIdentifier.trim()}
            onClick={() => createWave.mutate()}>
            {t("preparation.actions.createWaveConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <PreparationTaskDialog task={selectedTask}
        preparationHuDocumentEnabled={settings.data?.find((entry) =>
          entry.key === "PICKING_PREPARATION_HU_DOCUMENT_GENERATION_ENABLED")?.value === "true"}
        preparationHuScanRequired={settings.data?.find((entry) =>
          entry.key === "PICKING_PREPARATION_HU_SCAN_REQUIRED")?.value === "true"}
        onClose={() => setSelectedTask(null)}
        onSaved={refresh} />
    </Stack>
  );
}

function PreparationTaskDialog({
  task, preparationHuDocumentEnabled, preparationHuScanRequired, onClose, onSaved
}: {
  task: PreparationTask | null;
  preparationHuDocumentEnabled: boolean;
  preparationHuScanRequired: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const [location, setLocation] = useState("");
  const [hu, setHu] = useState("");
  const [outputHu, setOutputHu] = useState("");
  const [preparedOutputNumber, setPreparedOutputNumber] = useState<string | null>(null);
  const [preparedOutputId, setPreparedOutputId] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [pickedQuantity, setPickedQuantity] = useState("");
  const [exceptionMode, setExceptionMode] = useState(false);
  const [exceptionCode, setExceptionCode] = useState("STOCK_NOT_FOUND");
  const [exceptionComment, setExceptionComment] = useState("");
  const [resolutionComment, setResolutionComment] = useState("");
  const [fefoRequestReason, setFefoRequestReason] = useState("");
  const [fefoRequestSubmitted, setFefoRequestSubmitted] = useState(false);
  const [handlingEquipmentId, setHandlingEquipmentId] = useState("");
  const [handlingEquipmentIdentifier, setHandlingEquipmentIdentifier] = useState("");
  const [noEquipmentRequired, setNoEquipmentRequired] = useState(false);
  const automaticPreparationStarted = useRef<string | null>(null);
  const handlingEquipment = useQuery({
    queryKey: ["picking-handling-equipment", task?.id],
    queryFn: () => loadPickingHandlingEquipment(task!.id),
    enabled: Boolean(task?.id && task.status === "OPEN")
  });
  useEffect(() => {
    setLocation("");
    setHu("");
    setOutputHu("");
    setPreparedOutputNumber(task?.outputHandlingUnitNumber ?? null);
    setPreparedOutputId(task?.outputHandlingUnitId ?? null);
    setCurrentVersion(task?.version ?? 0);
    setPickedQuantity(task ? String(task.requestedQuantity) : "");
    setExceptionMode(false);
    setExceptionCode("STOCK_NOT_FOUND");
    setExceptionComment("");
    setResolutionComment("");
    setFefoRequestReason("");
    setFefoRequestSubmitted(false);
    setHandlingEquipmentId("");
    setHandlingEquipmentIdentifier("");
    setNoEquipmentRequired(false);
  }, [task?.id]);
  const take = useMutation({
    mutationFn: () => takePreparationTask(
      task!.id, task!.version,
      noEquipmentRequired ? null : handlingEquipmentId,
      noEquipmentRequired,
      noEquipmentRequired ? null : handlingEquipmentIdentifier.trim()
    ),
    onSuccess: onSaved
  });
  const complete = useMutation({
    mutationFn: () => completePreparationTask(task!.id, {
      sourceLocationCode: location.trim(),
      handlingUnitNumber: hu.trim(),
      outputHandlingUnitNumber: outputHu.trim() || null,
      pickedQuantity: Number(pickedQuantity),
      version: currentVersion
    }),
    onSuccess: onSaved
  });
  const prepareOutput = useMutation({
    mutationFn: (identifier: string | null) => prepareOutputHandlingUnit(
      task!.id, currentVersion, identifier
    ),
    onSuccess: (prepared) => {
      setPreparedOutputNumber(prepared.outputHandlingUnitNumber);
      setPreparedOutputId(prepared.outputHandlingUnitId);
      setCurrentVersion(prepared.version);
    }
  });
  useEffect(() => {
    if (!task || currentVersion < 1
      || automaticPreparationStarted.current === task.id) return;
    const ensureExistingDocument = Boolean(
      preparedOutputId && preparationHuDocumentEnabled
    );
    const prepareActiveOutput = task.status === "IN_PROGRESS"
      && !preparedOutputId
      && !(preparationHuScanRequired && !preparationHuDocumentEnabled);
    if (!ensureExistingDocument && !prepareActiveOutput) return;
    automaticPreparationStarted.current = task.id;
    prepareOutput.mutate(null);
  }, [task?.id, task?.status, preparedOutputId, currentVersion,
    preparationHuScanRequired, preparationHuDocumentEnabled]);
  const openOutputDocument = useMutation({
    mutationFn: async () => {
      if (!preparedOutputId) throw new Error(t("preparation.errors.documentUnavailable"));
      const documents = await loadHandlingUnitDocuments(preparedOutputId);
      const document = documents[0];
      if (!document) throw new Error(t("preparation.errors.documentUnavailable"));
      const preview = window.open("", "_blank");
      try {
        const blob = await loadHandlingUnitDocumentContent(preparedOutputId, document.id);
        const url = URL.createObjectURL(blob);
        if (preview) preview.location.href = url;
        else window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (cause) {
        preview?.close();
        throw cause;
      }
    }
  });
  const exception = useMutation({
    mutationFn: () => reportPreparationException(task!.id, {
      code: exceptionCode,
      comment: exceptionComment.trim(),
      version: task!.version
    }),
    onSuccess: onSaved
  });
  const resolveException = useMutation({
    mutationFn: (action: "RETRY" | "CANCEL_AND_RELEASE") =>
      resolvePreparationException(task!.id, {
        action,
        comment: resolutionComment.trim(),
        version: task!.version
      }),
    onSuccess: onSaved
  });
  const fefoRequest = useMutation({
    mutationFn: () => requestFefoException(task!.id, fefoRequestReason.trim()),
    onSuccess: async () => {
      take.reset();
      complete.reset();
      setFefoRequestSubmitted(true);
      await onSaved();
    }
  });
  if (!task) return null;
  const error = take.error ?? complete.error ?? prepareOutput.error
    ?? openOutputDocument.error ?? exception.error
    ?? resolveException.error ?? fefoRequest.error;
  const fefoViolation = (take.error instanceof ApiError || complete.error instanceof ApiError)
    && String((take.error ?? complete.error) instanceof Error
      ? (take.error ?? complete.error)!.message : "").toUpperCase().includes("FEFO");
  const fefoRequestPending = fefoViolation && String(
    (take.error ?? complete.error) instanceof Error
      ? (take.error ?? complete.error)!.message : ""
  ).toUpperCase().includes("FEFO_REQUEST_PENDING");

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack spacing={.5}>
          <Stack direction="row" alignItems="center"
            justifyContent="space-between" spacing={1}>
            <Typography component="span" variant="h6" fontWeight={850}>
              {t("preparation.labels.picking")}
            </Typography>
            <Chip size="small" label={operationalStatusLabel(task.status)}
              color={operationalStatusColor(task.status)} />
          </Stack>
          <Typography component="span" variant="body2" color="text.secondary"
            fontWeight={700}>
            {task.taskNumber}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box sx={{ p: 1.5, border: 1, borderColor: "divider",
            borderRadius: 1.5, display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("preparation.labels.hu")}
              </Typography>
              <Typography fontWeight={800}>{task.handlingUnitNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("preparation.labels.item")}
              </Typography>
              <Typography fontWeight={750}>
                {task.itemCode} · {task.itemName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("preparation.labels.quantity")}
              </Typography>
              <Typography fontWeight={750}>
                {quantity(task.requestedQuantity, locale)} {task.uom}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("preparation.labels.routeField")}
              </Typography>
              <Typography fontWeight={750}>
                {task.sourceLocationCode} → {task.destinationProcessPointCode}
              </Typography>
            </Box>
          </Box>
          {task.status === "OPEN" && (
            <Alert severity="info" sx={{ display: { xs: "none", sm: "flex" } }}>
              {t("preparation.help.directEquipment")}
            </Alert>
          )}
          {task.status === "IN_PROGRESS" && !exceptionMode && (
            <Alert severity="info" sx={{ display: { xs: "none", sm: "flex" } }}>
              {t("preparation.help.scanTask")}
            </Alert>
          )}
          {task.status === "OPEN" && (
            <>
              <ScanInput
                disabled={noEquipmentRequired || handlingEquipment.isLoading}
                options={(handlingEquipment.data ?? []).filter((item) => item.suitable)
                  .map((item) => ({ value: item.equipmentNumber,
                    label: `${item.equipmentNumber} · ${item.typeName}`
                      + ` · ${t("preparation.labels.load", { load: formatMeasurement(
                        item.requiredLoadKg, "MASS", measurements, locale) })}` }))}
                value={handlingEquipmentIdentifier}
                onChange={(value) => {
                  setHandlingEquipmentIdentifier(value);
                  const selected = (handlingEquipment.data ?? []).find((item) =>
                    item.equipmentNumber.toUpperCase() === value.trim().toUpperCase());
                  setHandlingEquipmentId(selected?.id ?? "");
                }}
                required={!noEquipmentRequired}
                label={t("preparation.labels.handlingEquipment")}
                helperText={t("preparation.help.compatibleEquipment")}
              />
              <FormControlLabel control={<Checkbox
                checked={noEquipmentRequired}
                onChange={(event) => {
                  setNoEquipmentRequired(event.target.checked);
                  if (event.target.checked) {
                    setHandlingEquipmentId("");
                    setHandlingEquipmentIdentifier("");
                  }
                }} />}
                label={t("preparation.labels.noEquipment")} />
              {handlingEquipment.isError && (
                <Alert severity="error">
                  {t("preparation.errors.availableEquipment")}
                </Alert>
              )}
            </>
          )}
          {task.status === "IN_PROGRESS" && !exceptionMode && (
            <>
              {task.derogatedLotVisualCheckRequired && (
                <Alert severity="warning" data-testid="derogated-lot-visual-check">
                  {t("preparation.help.derogatedLotVisualCheck")}
                </Alert>
              )}
              <ScanInput required label={t("preparation.scan.sourceLocation")}
                testId="preparation-source-scan"
                value={location}
                onChange={setLocation} />
              <ScanInput required label={t("preparation.scan.hu")}
                testId="preparation-hu-scan"
                value={hu} onChange={setHu} />
              {preparationHuScanRequired && (
                preparedOutputNumber ? (
                  <>
                    <Alert severity="success">
                      {t("preparation.help.outputHuPrepared", { number: preparedOutputNumber })}
                    </Alert>
                    <ScanInput required label={t("preparation.scan.outputHu")}
                      testId="preparation-output-hu-scan"
                      value={outputHu} onChange={setOutputHu} />
                  </>
                ) : (
                  preparationHuDocumentEnabled ? (
                    <Alert severity="info">{t("preparation.help.preparingOutputHu")}</Alert>
                  ) : (
                    <>
                      <ScanInput required label={t("preparation.scan.outputHu")}
                        testId="preparation-output-hu-scan"
                        value={outputHu} onChange={setOutputHu} />
                      <Button variant="outlined"
                        onClick={() => prepareOutput.mutate(outputHu.trim())}
                        disabled={prepareOutput.isPending || !outputHu.trim()}>
                        {t("preparation.actions.reserveReturnableHu")}
                      </Button>
                    </>
                  )
                )
              )}
              <TextField required type="number" label={t("preparation.labels.pickedQuantity")}
                data-testid="preparation-picked-quantity"
                value={pickedQuantity}
                inputProps={{ min: .000001, step: "any" }}
                onChange={(event) => setPickedQuantity(event.target.value)} />
            </>
          )}
          {exceptionMode && (
            <>
              <TextField select label={t("preparation.labels.exceptionType")}
                data-testid="preparation-exception-code"
                value={exceptionCode}
                onChange={(event) => setExceptionCode(event.target.value)}>
                <MenuItem value="STOCK_NOT_FOUND">{t("preparation.exceptions.STOCK_NOT_FOUND")}</MenuItem>
                <MenuItem value="QUANTITY_MISMATCH">{t("preparation.exceptions.QUANTITY_MISMATCH")}</MenuItem>
                <MenuItem value="HU_DAMAGED">{t("preparation.exceptions.HU_DAMAGED")}</MenuItem>
                <MenuItem value="LOCATION_INACCESSIBLE">
                  {t("preparation.exceptions.LOCATION_INACCESSIBLE")}
                </MenuItem>
              </TextField>
              <TextField required multiline minRows={3}
                data-testid="preparation-exception-comment"
                label={t("preparation.labels.exceptionComment")}
                value={exceptionComment}
                onChange={(event) => setExceptionComment(event.target.value)}
                helperText={t("preparation.help.requiredThree")} />
            </>
          )}
          {task.status === "EXCEPTION" && (
            <>
              <Alert severity="error">
                {t(`preparation.exceptions.${task.exceptionCode}`, {
                  defaultValue: task.exceptionCode
                })} — {task.exceptionComment}
              </Alert>
              <TextField required multiline minRows={2}
                data-testid="preparation-resolution-comment"
                label={t("preparation.labels.resolutionComment")}
                value={resolutionComment}
                onChange={(event) => setResolutionComment(event.target.value)}
                helperText={t("preparation.help.requiredThree")} />
            </>
          )}
          {error && !fefoViolation && <Alert severity="error">
            {error instanceof Error ? error.message : t("preparation.errors.action")}
          </Alert>}
          {fefoViolation && !fefoRequestSubmitted && !fefoRequestPending && (
            <Alert severity="warning">
              {t("preparation.fefo.blocked")}
            </Alert>
          )}
          {fefoViolation && !fefoRequestSubmitted && !fefoRequestPending && (
            <TextField required multiline minRows={3}
              data-testid="preparation-fefo-exception-reason"
              label={t("preparation.fefo.reason")}
              value={fefoRequestReason}
              onChange={(event) => setFefoRequestReason(event.target.value)}
              helperText={t("preparation.help.requiredThree")} />
          )}
          {(fefoRequestSubmitted || fefoRequestPending) && (
            <Alert severity="error"
              sx={{ border: 2, borderColor: "error.main" }}>
              {t("preparation.fefo.submitted")}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 3 }, pb: { xs: 2.5, sm: 2 },
        display: "flex", flexDirection: { xs: "column-reverse", sm: "row" },
        alignItems: "stretch", gap: 1,
        "& > :not(style)": {
          width: { xs: "100%", sm: "auto" },
          minHeight: { xs: 48, sm: 40 },
          mx: "0 !important",
          whiteSpace: "normal"
        }
      }}>
        <Button onClick={onClose}>{t("common.actions.close")}</Button>
        {preparationHuDocumentEnabled && preparedOutputId && (
          <Button variant="outlined" startIcon={<DescriptionOutlined />}
            disabled={openOutputDocument.isPending}
            onClick={() => openOutputDocument.mutate()}>
            {t("preparation.actions.viewOutputHuDocument")}
          </Button>
        )}
        {task.status === "OPEN" && (
          <Button variant="contained"
            data-testid="preparation-take-confirm"
            disabled={take.isPending
              || (!noEquipmentRequired && !handlingEquipmentIdentifier.trim())}
            onClick={() => take.mutate()}>
            {t("preparation.actions.confirmTake")}
          </Button>
        )}
        {task.status === "IN_PROGRESS" && !exceptionMode && (
          <>
            <Button color="error" data-testid="preparation-report-problem"
              variant="outlined"
              onClick={() => setExceptionMode(true)}>
              {t("preparation.actions.reportProblem")}
            </Button>
            <Button variant="contained" color="success"
              data-testid="preparation-complete-confirm"
              disabled={!location.trim() || !hu.trim() || !pickedQuantity
                || !preparedOutputNumber
                || (preparationHuScanRequired
                  && (!preparedOutputNumber || !outputHu.trim()))}
              onClick={() => complete.mutate()}>
              {t("preparation.actions.confirmPicking")}
            </Button>
          </>
        )}
        {exceptionMode && (
          <Button variant="contained" color="error"
            data-testid="preparation-report-confirm"
            disabled={exceptionComment.trim().length < 3}
            onClick={() => exception.mutate()}>
            {t("preparation.actions.saveAlert")}
          </Button>
        )}
        {task.status === "EXCEPTION" && (
          <>
            <Button disabled={resolutionComment.trim().length < 3}
              onClick={() => resolveException.mutate("CANCEL_AND_RELEASE")}>
              {t("preparation.actions.cancelRelease")}
            </Button>
            <Button variant="contained"
              data-testid="preparation-retry-confirm"
              disabled={resolutionComment.trim().length < 3}
              onClick={() => resolveException.mutate("RETRY")}>
              {t("common.actions.retry")}
            </Button>
          </>
        )}
        {fefoViolation && !fefoRequestSubmitted && !fefoRequestPending && (
          <Button variant="contained" color="warning"
            data-testid="preparation-request-fefo-exception"
            disabled={fefoRequest.isPending || fefoRequestReason.trim().length < 3}
            onClick={() => fefoRequest.mutate()}>
            {t("preparation.actions.requestFefoException")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
